import { Breakpoint } from '@mui/material';
import { createContext, FC, ReactNode, useContext, useState } from 'react';

interface DialogContent {
  title: {
    text: string;
    icon?: ReactNode;
  };
  contentComponent: ReactNode;
  size?: Breakpoint;
}

interface DialogContextType {
  isOpenDialog: boolean;
  dialogContent: DialogContent | null;
  openDialog: (
    title: {
      text: string;
      icon?: ReactNode;
    },
    contentComponent: ReactNode,
    size?: Breakpoint,
  ) => void;
  closeDialog: () => void;
}

const DialogContext = createContext<DialogContextType | undefined>(undefined);

interface DialogContextProviderProps {
  children: ReactNode;
}

const DialogContextProvider: FC<DialogContextProviderProps> = ({ children }) => {
  const [isOpenDialog, setIsOpenDialog] = useState<boolean>(false);
  const [dialogContent, setDialogContent] = useState<DialogContent | null>(null);

  const openDialog = (
    title: {
      text: string;
      icon?: ReactNode;
    },
    contentComponent: ReactNode,
    size: Breakpoint = 'sm',
  ) => {
    setDialogContent({ contentComponent, title, size });
    setIsOpenDialog(true);
  };

  const closeDialog = () => {
    setIsOpenDialog(false);
    setDialogContent(null);
  };

  return (
    <DialogContext.Provider
      value={{
        isOpenDialog,
        dialogContent,
        openDialog,
        closeDialog,
      }}
    >
      {children}
    </DialogContext.Provider>
  );
};

export const useDialogContext = (): DialogContextType => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialogContext must be used within a DialogContextProvider');
  }
  return context;
};

export default DialogContextProvider;
